'use client';

import IconMoreVertical from '@mnd-frontend/icons/components/MoreVertical';
import {
  GREY_100,
  GREY_400,
  GREY_1000,
  NEUTRAL_WHITE,
  PURPLE_400,
  SHADOW_1,
} from '@mnd-frontend/ui/shared/tokens';
import type { ReactElement, ReactNode } from 'react';
import { Menu, MenuItem, MenuTrigger, Popover, Pressable, Separator } from 'react-aria-components';
import styled from 'styled-components';

const StyledButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
`;

const StyledMenu = styled(Menu)`
  padding: 4px 0;
  min-width: 208px;
  background-color: ${NEUTRAL_WHITE};
  border: 2px solid ${PURPLE_400};
  border-radius: 8px;
  box-shadow: ${SHADOW_1};
`;

const StyledMenuItem = styled(MenuItem)<{ $color?: string }>`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: ${props => (props.$color ? props.$color : GREY_1000)};

  &:hover {
    background-color: ${GREY_100};
  }
`;

const StyledSeparator = styled(Separator)`
  height: 1px;
  background-color: ${GREY_400};
`;

export type DropdownItem =
  | { separator: boolean }
  | ({
      onAction: () => void;
      color?: string;
      separator?: boolean;
    } & ({ label: string; icon?: ReactNode } | { icon: ReactNode; label?: string }));

export interface DropdownProps {
  items?: DropdownItem[];
  trigger?: ReactElement;
}

const Dropdown = ({ items = [], trigger }: DropdownProps) => {
  if (items.length === 0) {
    return null;
  }

  const triggerEl = trigger ?? (
    <StyledButton aria-label="Menu" type="button">
      <IconMoreVertical />
    </StyledButton>
  );

  return (
    <MenuTrigger>
      <Pressable>{triggerEl}</Pressable>
      <Popover>
        <StyledMenu>
          {items.map((item, index) =>
            'separator' in item && item.separator === true ? (
              <StyledSeparator key={index} />
            ) : (
              <StyledMenuItem
                key={index}
                onAction={'onAction' in item ? item.onAction : undefined}
                $color={'color' in item ? item.color : undefined}
              >
                {'icon' in item ? item.icon : null}
                {'label' in item ? item.label : null}
              </StyledMenuItem>
            ),
          )}
        </StyledMenu>
      </Popover>
    </MenuTrigger>
  );
};

export default Dropdown;
